<template>
  <div class="maintenance-page">
    <Sidebar />

    <div class="main">
      <Topbar />

      <div class="content">
        <div class="page-header-row">
          <div class="page-header-left">
            <div v-if="topbarForwardingEmail" class="email-pill">
              <svg viewBox="0 0 14 14" fill="none" stroke="#8b8aaa" stroke-width="1.8"><rect x="1" y="3" width="12" height="9" rx="1"/><path d="M1 4l6 5 6-5" stroke-linecap="round" stroke-linejoin="round"/></svg>
              {{ topbarForwardingEmail }}
              <button class="copy-btn" @click="copyEmail(topbarForwardingEmail, $event)" aria-label="Copy email address">
                <svg viewBox="0 0 14 14" fill="none" stroke="currentColor" stroke-width="1.8"><rect x="4" y="4" width="9" height="9" rx="1"/><path d="M1 10V2a1 1 0 0 1 1-1h8" stroke-linecap="round"/></svg>
              </button>
            </div>
          </div>
          <button class="btn-add" @click="openAddModal">
            <svg viewBox="0 0 14 14" fill="none" stroke="white" stroke-width="2"><line x1="7" y1="1" x2="7" y2="13" stroke-linecap="round"/><line x1="1" y1="7" x2="13" y2="7" stroke-linecap="round"/></svg>
            Add manually
          </button>
        </div>

        <div class="intake-info">
          <svg width="16" height="16" viewBox="0 0 14 14" fill="none" stroke="#3c3489" stroke-width="1.8" stroke-linecap="round" class="intake-info-icon"><circle cx="7" cy="7" r="6"/><line x1="7" y1="6" x2="7" y2="10"/><circle cx="7" cy="4" r="0.5" fill="#3c3489" stroke="none"/></svg>
          <div style="flex:1;">
            <div class="intake-info-title">How to get invoices into this page</div>
            <div class="intake-info-body">When you receive a maintenance invoice, forward the email to your unique address below — make sure the PDF invoice is attached. It will appear in the queue automatically for you to allocate to a property. You can also add costs manually using the button above.</div>
            <div class="intake-info-row">
              <span class="intake-info-label">Your forwarding address:</span>
              <span class="intake-info-email">
                {{ forwardingEmail }}
                <button @click="copyEmail(forwardingEmail, $event)" aria-label="Copy email address" class="intake-info-copy">
                  <svg width="12" height="12" viewBox="0 0 14 14" fill="none" stroke="currentColor" stroke-width="1.8"><rect x="4" y="4" width="9" height="9" rx="1"/><path d="M1 10V2a1 1 0 0 1 1-1h8" stroke-linecap="round"/></svg>
                </button>
              </span>
            </div>
            <button class="setup-toggle" @click="showSetup = !showSetup" :aria-expanded="showSetup">
              <svg viewBox="0 0 12 12" fill="none" stroke="currentColor" stroke-width="2" :class="{ 'setup-chevron-open': showSetup }" class="setup-chevron"><path d="M3 4.5l3 3 3-3" stroke-linecap="round" stroke-linejoin="round"/></svg>
              {{ showSetup ? 'Hide setup instructions' : 'Setup instructions' }}
            </button>
            <div v-if="showSetup" class="setup-panel">
              <div class="setup-step">
                <div class="setup-step-num">1</div>
                <div class="setup-step-body">
                  <div class="setup-step-title">Automatic forwarding from your supplier inbox</div>
                  <div class="setup-step-text">In Gmail, Outlook or your provider, create a filter that automatically forwards emails from your maintenance suppliers (plumber, electrician, gardener, etc.) to <strong>{{ forwardingEmail || 'your forwarding address above' }}</strong>. Make sure "forward with attachments" is enabled.</div>
                </div>
              </div>
              <div class="setup-step">
                <div class="setup-step-num">2</div>
                <div class="setup-step-body">
                  <div class="setup-step-title">Or forward each invoice manually</div>
                  <div class="setup-step-text">Open the supplier's email in your inbox and hit <strong>Forward</strong>. Send it to your address above. The PDF attachment is required — it becomes the receipt attached to the property expense.</div>
                </div>
              </div>
              <div class="setup-step">
                <div class="setup-step-num">3</div>
                <div class="setup-step-body">
                  <div class="setup-step-title">What arrives in your queue</div>
                  <div class="setup-step-text">Within a few seconds the invoice appears in <strong>Invoice queue</strong> below with supplier name, amount and the attached PDF. Pick the property in the dropdown and click <strong>Allocate</strong> to send it to that month's report.</div>
                </div>
              </div>
              <div class="setup-note">
                Tips: if a supplier sends the invoice as body text rather than PDF, use <strong>Add manually</strong>. If a forwarded email doesn't appear within a minute, check that the PDF was actually attached (some providers strip attachments on forward).
              </div>
            </div>
          </div>
        </div>

        <div>
          <div class="section-label">Incoming invoices — needs allocating</div>
          <div class="queue-card">
            <div class="queue-header">
              <div class="queue-title">Invoice queue</div>
              <span class="badge-count" :class="{ 'badge-empty': pendingInvoices.length === 0 }">{{ pendingInvoices.length === 0 ? 'All clear' : pendingInvoices.length + ' pending' }}</span>
            </div>
            <div v-if="pendingInvoices.length === 0" class="empty-queue">
              <svg viewBox="0 0 20 20" fill="none" stroke="#0f6e56" stroke-width="1.8"><polyline points="4,10 8,14 16,6" stroke-linecap="round" stroke-linejoin="round"/><circle cx="10" cy="10" r="9"/></svg>
              All invoices allocated
            </div>
            <div v-else>
              <div class="invoice-row" v-for="inv in pendingInvoices" :key="inv.id">
                <div>
                  <div class="inv-from">{{ inv.supplier }}</div>
                  <div class="inv-sub">
                    {{ inv.description }}
                    <a v-if="inv.attachment_url" :href="inv.attachment_url" target="_blank" class="inv-attach">
                      <svg viewBox="0 0 12 12" fill="none" stroke="currentColor" stroke-width="2"><path d="M2 6l3 3 5-5" stroke-linecap="round" stroke-linejoin="round"/><path d="M4 1H2a1 1 0 0 0-1 1v8a1 1 0 0 0 1 1h8a1 1 0 0 0 1-1V7" stroke-linecap="round"/></svg>
                      {{ inv.attachment_filename }}
                    </a>
                  </div>
                </div>
                <div>
                  <div class="inv-amount">{{ formatMoney(inv.amount, inv.currency) }}</div>
                  <div class="inv-date">Received {{ formatDate(inv.received_at) }}</div>
                </div>
                <div>
                  <select class="prop-select" v-model="selectedPropertyByInvoice[inv.id]" :class="{ error: errorPropertyForInvoice === inv.id }">
                    <option value="">Select property or group…</option>
                    <option v-for="p in properties" :key="'p'+p.id" :value="String(p.id)">{{ p.name }}</option>
                    <option v-if="groups.length" disabled>─── Groups ───</option>
                    <option v-for="g in groups" :key="'g'+g.id" :value="'group:'+g.id">{{ g.name }}</option>
                  </select>
                </div>
                <div class="row-actions">
                  <button class="btn-dismiss" :disabled="busyInvoiceId === inv.id" @click="dismissInvoice(inv)">Dismiss</button>
                  <button class="btn-allocate" :disabled="busyInvoiceId === inv.id" @click="openAllocateModal(inv)">Allocate</button>
                  <button class="btn-delete" :disabled="busyInvoiceId === inv.id" @click="deleteInvoice(inv, 'pending')" aria-label="Delete">
                    <svg viewBox="0 0 14 14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="2,4 12,4"/><path d="M3 4v8a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1V4"/><path d="M5 4V3a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1v1"/></svg>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div>
          <div class="section-label">Allocated costs — added to reports</div>
          <div class="log-card">
            <div class="log-toolbar">
              <div class="log-title">{{ filterLabel }}</div>
              <select v-model="filterProperty" class="log-filter">
                <option value="all">All properties</option>
                <option v-for="p in properties" :key="'fp'+p.id" :value="String(p.id)">{{ p.name }}</option>
                <option v-if="groups.length" disabled>─── Groups ───</option>
                <option v-for="g in groups" :key="'fg'+g.id" :value="'group:'+g.id">{{ g.name }}</option>
              </select>
            </div>
            <div class="col-heads">
              <div class="ch">Supplier / description</div>
              <div class="ch">Property</div>
              <div class="ch">Amount</div>
              <div class="ch">Date</div>
              <div class="ch"></div>
            </div>
            <div v-if="filteredAllocated.length === 0" class="empty-queue">
              No allocated costs yet
            </div>
            <div v-else>
              <div class="log-row" v-for="inv in filteredAllocated" :key="inv.id">
                <div>
                  <div class="lr-supplier">{{ inv.supplier }}</div>
                  <div class="lr-desc">{{ inv.description }}</div>
                </div>
                <div class="lr-prop">
                  <span>{{ inv.target_display || inv.property_name || inv.property_id }}</span>
                  <span v-if="inv.target_kind === 'group'" class="group-tag">Group</span>
                </div>
                <div class="lr-amount">{{ formatMoney(inv.amount, inv.currency) }}</div>
                <div class="lr-date">{{ formatDate(inv.received_at) }}</div>
                <div class="lr-actions">
                  <button class="btn-delete" :disabled="busyInvoiceId === inv.id" @click="deleteInvoice(inv, 'allocated')" aria-label="Delete">
                    <svg viewBox="0 0 14 14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="2,4 12,4"/><path d="M3 4v8a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1V4"/><path d="M5 4V3a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1v1"/></svg>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

      </div>
    </div>

    <div v-if="showAllocateModal" class="modal-overlay" @click.self="closeAllocateModal">
      <div class="modal-card">
        <div class="modal-header">
          <div class="modal-title">Allocate maintenance cost</div>
          <button class="modal-close" @click="closeAllocateModal" aria-label="Close">
            <svg viewBox="0 0 14 14" fill="none" stroke="currentColor" stroke-width="2"><line x1="3" y1="3" x2="11" y2="11"/><line x1="11" y1="3" x2="3" y2="11"/></svg>
          </button>
        </div>
        <div class="modal-body">
          <div class="allocate-summary">
            <div class="allocate-summary-row">
              <span class="allocate-summary-label">Supplier</span>
              <span class="allocate-summary-value">{{ allocateInv ? allocateInv.supplier : '' }}</span>
            </div>
            <div class="allocate-summary-row">
              <span class="allocate-summary-label">Invoice amount</span>
              <span class="allocate-summary-value">{{ allocateInv ? formatMoney(allocateInv.amount, allocateInv.currency) : '' }}</span>
            </div>
            <div class="allocate-summary-row">
              <span class="allocate-summary-label">Allocate to</span>
              <span class="allocate-summary-value">{{ allocateTargetName }}</span>
            </div>
          </div>

          <div class="form-row">
            <label>Report month</label>
            <div class="form-row-inline">
              <select v-model.number="allocateForm.month" class="prop-select">
                <option v-for="(mLabel, i) in monthNames" :key="'m'+i" :value="i + 1">{{ mLabel }}</option>
              </select>
              <select v-model.number="allocateForm.year" class="prop-select">
                <option v-for="y in yearOptions" :key="'y'+y" :value="y">{{ y }}</option>
              </select>
            </div>
            <p class="form-hint">Pick the month this cost belongs to (defaults to the invoice received date).</p>
          </div>

          <div class="form-row">
            <label>VAT Treatment</label>
            <div class="vat-toggle-group">
              <label class="vat-toggle-option" :class="{ active: !allocateForm.is_vat_applicable }">
                <input type="radio" :value="false" v-model="allocateForm.is_vat_applicable" style="display:none;">
                No VAT &mdash; Agent / Disbursement
              </label>
              <label class="vat-toggle-option" :class="{ active: allocateForm.is_vat_applicable }">
                <input type="radio" :value="true" v-model="allocateForm.is_vat_applicable" style="display:none;">
                VAT Applicable &mdash; Supplied Service
              </label>
            </div>
            <p class="form-hint">Select "VAT Applicable" if this cost is supplied by your company and VAT must be charged to the owner.</p>
          </div>

          <div v-if="allocateForm.is_vat_applicable" class="form-row">
            <label>Amount Entry Method</label>
            <select v-model="allocateForm.vat_treatment" class="prop-select">
              <option value="gross">Gross Amount (VAT included)</option>
              <option value="net_plus_vat">Net + VAT Separated</option>
            </select>
            <p v-if="allocateForm.vat_treatment === 'gross'" class="form-hint">
              Uses the invoice amount as gross (VAT included) — net and VAT are extracted automatically at {{ allocateForm.vat_rate }}%.
            </p>
          </div>

          <div v-if="allocateForm.is_vat_applicable && allocateForm.vat_treatment === 'net_plus_vat'" class="form-row-inline">
            <div class="form-row">
              <label>Net Amount (£)</label>
              <input v-model.number="allocateForm.net_amount" type="number" step="0.01" min="0" placeholder="0.00" />
            </div>
            <div class="form-row">
              <label>VAT Amount (£)</label>
              <input v-model.number="allocateForm.vat_amount" type="number" step="0.01" min="0" placeholder="0.00" />
            </div>
          </div>

          <div v-if="allocateFormError" class="form-error">{{ allocateFormError }}</div>
        </div>
        <div class="modal-footer">
          <button class="btn-cancel" @click="closeAllocateModal">Cancel</button>
          <button class="btn-primary" :disabled="allocateSubmitting" @click="submitAllocate">
            {{ allocateSubmitting ? 'Allocating…' : 'Allocate to report' }}
          </button>
        </div>
      </div>
    </div>

    <div v-if="showAddModal" class="modal-overlay" @click.self="closeAddModal">
      <div class="modal-card">
        <div class="modal-header">
          <div class="modal-title">Add maintenance cost</div>
          <button class="modal-close" @click="closeAddModal" aria-label="Close">
            <svg viewBox="0 0 14 14" fill="none" stroke="currentColor" stroke-width="2"><line x1="3" y1="3" x2="11" y2="11"/><line x1="11" y1="3" x2="3" y2="11"/></svg>
          </button>
        </div>
        <div class="modal-body">
          <div class="form-row">
            <label>Supplier</label>
            <input v-model="addForm.supplier" type="text" placeholder="e.g. Dorset Plumbing Services" />
          </div>
          <div class="form-row">
            <label>Description</label>
            <input v-model="addForm.description" type="text" placeholder="e.g. Boiler service &amp; pressure check" />
          </div>
          <div class="form-row-inline">
            <div class="form-row">
              <label>Amount</label>
              <input v-model.number="addForm.amount" type="number" step="0.01" placeholder="0.00" />
            </div>
            <div class="form-row">
              <label>Received on</label>
              <input v-model="addForm.received_at" type="date" />
            </div>
          </div>
          <div class="form-row">
            <label>Attachment (PDF, optional)</label>
            <input type="file" accept="application/pdf,image/*" @change="onFileChange" />
            <div v-if="addForm.attachment" class="file-hint">{{ addForm.attachment.name }}</div>
          </div>
          <div v-if="addFormError" class="form-error">{{ addFormError }}</div>
        </div>
        <div class="modal-footer">
          <button class="btn-cancel" @click="closeAddModal">Cancel</button>
          <button class="btn-primary" :disabled="submitting" @click="submitAdd">{{ submitting ? 'Saving…' : 'Save' }}</button>
        </div>
      </div>
    </div>

  </div>
</template>

<script>
import axios from 'axios';
import Sidebar from '@/components/Sidebar.vue';
import Topbar from '@/components/Topbar.vue';
import SETTINGS from '@/config/settings.js';
import maintenance from '@/services/maintenance.js';

export default {
  name: 'MaintenanceView',
  components: { Sidebar, Topbar },
  data() {
    return {
      baseUrl: SETTINGS.API.baseUrl,
      // Populated on load from GET /api/pms/maintenance/inbound-address/ — one
      // unique address per user (maintenance@<slug>.invoxis.co.uk).
      topbarForwardingEmail: '',
      forwardingEmail: '',

      properties: [],
      groups: [],
      pendingInvoices: [],
      allocatedInvoices: [],

      selectedPropertyByInvoice: {},
      errorPropertyForInvoice: null,
      busyInvoiceId: null,
      showSetup: false,

      filterProperty: 'all',

      showAddModal: false,
      submitting: false,
      addFormError: '',
      addForm: this.emptyAddForm(),

      showAllocateModal: false,
      allocateSubmitting: false,
      allocateFormError: '',
      allocateInv: null,
      allocateTarget: null,
      allocateForm: this.emptyAllocateForm(),
      monthNames: [
        'January', 'February', 'March', 'April', 'May', 'June',
        'July', 'August', 'September', 'October', 'November', 'December',
      ],
    };
  },
  computed: {
    filteredAllocated() {
      if (this.filterProperty === 'all') return this.allocatedInvoices;
      if (String(this.filterProperty).startsWith('group:')) {
        const gid = Number(String(this.filterProperty).slice(6));
        return this.allocatedInvoices.filter((inv) => Number(inv.property_group) === gid);
      }
      return this.allocatedInvoices.filter((inv) => String(inv.property_id) === String(this.filterProperty));
    },
    filterLabel() {
      if (this.filterProperty === 'all') return 'All properties';
      if (String(this.filterProperty).startsWith('group:')) {
        const gid = Number(String(this.filterProperty).slice(6));
        const g = this.groups.find((x) => Number(x.id) === gid);
        return g ? g.name : 'All properties';
      }
      return this.propertyNameById(this.filterProperty) || 'All properties';
    },
    yearOptions() {
      const currentYear = new Date().getFullYear();
      const invoiceYear = this.allocateInv && this.allocateInv.received_at
        ? new Date(this.allocateInv.received_at).getFullYear()
        : currentYear;
      const anchor = Math.max(currentYear, invoiceYear);
      return [anchor - 2, anchor - 1, anchor, anchor + 1];
    },
    allocateTargetName() {
      if (!this.allocateTarget) return '';
      if (this.allocateTarget.property_group_id) {
        const g = this.groups.find((x) => Number(x.id) === Number(this.allocateTarget.property_group_id));
        return g ? `${g.name} (Group)` : `Group #${this.allocateTarget.property_group_id}`;
      }
      return this.propertyNameById(this.allocateTarget.property_id) || this.allocateTarget.property_id;
    },
  },
  created() {
    this.fetchProperties();
    this.fetchGroups();
    this.fetchInvoices();
    this.fetchInboundAddress();
  },
  methods: {
    emptyAddForm() {
      const today = new Date().toISOString().slice(0, 10);
      return {
        supplier: '',
        description: '',
        amount: null,
        received_at: today,
        attachment: null,
      };
    },
    async fetchInboundAddress() {
      try {
        const res = await maintenance.inboundAddress();
        this.topbarForwardingEmail = res.address || '';
        this.forwardingEmail = res.address || '';
      } catch (e) {
        this.topbarForwardingEmail = '';
        this.forwardingEmail = '';
      }
    },
    async fetchProperties() {
      const selectedPms = JSON.parse(localStorage.getItem('selectedPmsConfig') || 'null');
      if (!selectedPms || !selectedPms.id) return;
      const token = localStorage.getItem('token');
      try {
        const res = await axios.get(`${this.baseUrl}/api/pms/properties/`, {
          headers: { Authorization: `Bearer ${token}` },
          params: { pms_config_id: selectedPms.id },
        });
        this.properties = (res.data && res.data.properties && res.data.properties.data) || [];
      } catch (e) {
        this.properties = [];
      }
    },
    async fetchGroups() {
      try {
        const res = await maintenance.groups();
        this.groups = (res && res.groups) || [];
      } catch (e) {
        this.groups = [];
      }
    },
    async fetchInvoices() {
      try {
        const [pending, allocated] = await Promise.all([
          maintenance.list({ status: 'pending' }),
          maintenance.list({ status: 'allocated' }),
        ]);
        this.pendingInvoices = pending;
        this.allocatedInvoices = allocated;
        pending.forEach((inv) => {
          if (this.selectedPropertyByInvoice[inv.id] === undefined) {
            this.selectedPropertyByInvoice[inv.id] = '';
          }
        });
      } catch (e) {
        this.pendingInvoices = [];
        this.allocatedInvoices = [];
      }
    },
    propertyNameById(id) {
      const p = this.properties.find((x) => String(x.id) === String(id));
      return p ? p.name : '';
    },
    formatMoney(amount, currency) {
      const symbol = currency === 'EUR' ? '€' : currency === 'USD' ? '$' : '£';
      const n = Number(amount || 0);
      return `${symbol}${n.toFixed(2)}`;
    },
    formatDate(iso) {
      if (!iso) return '';
      const d = new Date(iso);
      if (Number.isNaN(d.getTime())) return iso;
      return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
    },
    async copyEmail(text, evt) {
      try {
        await navigator.clipboard.writeText(text);
        const btn = evt.currentTarget;
        const svg = btn.querySelector('svg');
        if (svg) {
          const original = svg.style.stroke;
          svg.style.stroke = '#0f6e56';
          setTimeout(() => { svg.style.stroke = original; }, 1500);
        }
      } catch (e) { /* clipboard blocked, no-op */ }
    },
    emptyAllocateForm() {
      const now = new Date();
      return {
        year: now.getFullYear(),
        month: now.getMonth() + 1,
        is_vat_applicable: false,
        vat_treatment: 'gross',
        vat_rate: 20,
        net_amount: null,
        vat_amount: null,
      };
    },
    openAllocateModal(inv) {
      const raw = this.selectedPropertyByInvoice[inv.id];
      if (!raw) {
        this.errorPropertyForInvoice = inv.id;
        setTimeout(() => { this.errorPropertyForInvoice = null; }, 1500);
        return;
      }
      const target = String(raw).startsWith('group:')
        ? { property_group_id: Number(String(raw).slice(6)) }
        : { property_id: String(raw) };
      const received = inv.received_at ? new Date(inv.received_at) : new Date();
      const defaultMonth = Number.isNaN(received.getTime()) ? new Date().getMonth() + 1 : received.getMonth() + 1;
      const defaultYear = Number.isNaN(received.getTime()) ? new Date().getFullYear() : received.getFullYear();
      this.allocateInv = inv;
      this.allocateTarget = target;
      this.allocateForm = {
        ...this.emptyAllocateForm(),
        year: defaultYear,
        month: defaultMonth,
      };
      this.allocateFormError = '';
      this.showAllocateModal = true;
    },
    closeAllocateModal() {
      if (this.allocateSubmitting) return;
      this.showAllocateModal = false;
      this.allocateInv = null;
      this.allocateTarget = null;
    },
    async submitAllocate() {
      const form = this.allocateForm;
      if (form.is_vat_applicable && form.vat_treatment === 'net_plus_vat') {
        const net = Number(form.net_amount);
        const vat = Number(form.vat_amount);
        if (!(net > 0) || !(vat >= 0)) {
          this.allocateFormError = 'Enter both net and VAT amounts (net must be > 0).';
          return;
        }
      }
      const inv = this.allocateInv;
      const payload = {
        ...this.allocateTarget,
        year: form.year,
        month: form.month,
        is_vat_applicable: form.is_vat_applicable,
        vat_treatment: form.vat_treatment,
        vat_rate: form.vat_rate,
      };
      if (form.is_vat_applicable && form.vat_treatment === 'net_plus_vat') {
        payload.net_amount = Number(form.net_amount);
        payload.vat_amount = Number(form.vat_amount);
      }
      this.allocateSubmitting = true;
      this.busyInvoiceId = inv.id;
      try {
        const updated = await maintenance.allocate(inv.id, payload);
        this.pendingInvoices = this.pendingInvoices.filter((x) => x.id !== inv.id);
        this.allocatedInvoices = [updated, ...this.allocatedInvoices];
        delete this.selectedPropertyByInvoice[inv.id];
        this.showAllocateModal = false;
        this.allocateInv = null;
        this.allocateTarget = null;
      } catch (e) {
        this.allocateFormError = 'Failed to allocate. Please retry.';
      } finally {
        this.allocateSubmitting = false;
        this.busyInvoiceId = null;
      }
    },
    async dismissInvoice(inv) {
      this.busyInvoiceId = inv.id;
      try {
        await maintenance.dismiss(inv.id);
        this.pendingInvoices = this.pendingInvoices.filter((x) => x.id !== inv.id);
        delete this.selectedPropertyByInvoice[inv.id];
      } finally {
        this.busyInvoiceId = null;
      }
    },
    async deleteInvoice(inv, source) {
      const label = inv.supplier || 'this maintenance entry';
      const msg = source === 'allocated'
        ? `Delete "${label}"? This will also remove it from the report.`
        : `Delete "${label}" from the queue?`;
      if (!window.confirm(msg)) return;
      this.busyInvoiceId = inv.id;
      try {
        await maintenance.remove(inv.id);
        if (source === 'pending') {
          this.pendingInvoices = this.pendingInvoices.filter((x) => x.id !== inv.id);
          delete this.selectedPropertyByInvoice[inv.id];
        } else {
          this.allocatedInvoices = this.allocatedInvoices.filter((x) => x.id !== inv.id);
        }
      } catch (e) {
        window.alert('Failed to delete. Please try again.');
      } finally {
        this.busyInvoiceId = null;
      }
    },
    openAddModal() {
      this.addForm = this.emptyAddForm();
      this.addFormError = '';
      this.showAddModal = true;
    },
    closeAddModal() {
      this.showAddModal = false;
    },
    onFileChange(evt) {
      const file = evt.target.files && evt.target.files[0];
      this.addForm.attachment = file || null;
    },
    async submitAdd() {
      this.addFormError = '';
      if (!this.addForm.supplier.trim()) {
        this.addFormError = 'Supplier is required.';
        return;
      }
      if (!(this.addForm.amount > 0)) {
        this.addFormError = 'Amount must be greater than zero.';
        return;
      }
      if (!this.addForm.received_at) {
        this.addFormError = 'Date is required.';
        return;
      }
      this.submitting = true;
      try {
        const fd = new FormData();
        fd.append('supplier', this.addForm.supplier.trim());
        fd.append('description', this.addForm.description.trim());
        fd.append('amount', String(this.addForm.amount));
        fd.append('received_at', this.addForm.received_at);
        if (this.addForm.attachment) {
          fd.append('attachment', this.addForm.attachment);
          fd.append('attachment_filename', this.addForm.attachment.name);
        }
        const created = await maintenance.create(fd);
        this.pendingInvoices = [created, ...this.pendingInvoices];
        this.selectedPropertyByInvoice[created.id] = '';
        this.showAddModal = false;
      } catch (e) {
        this.addFormError = 'Failed to save. Please retry.';
      } finally {
        this.submitting = false;
      }
    },
  },
};
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600;700&display=swap');

.maintenance-page { font-family: 'DM Sans', sans-serif; background: #f4f4f8; color: #1c1b2e; min-height: 100vh; -webkit-font-smoothing: antialiased; }

.main { margin-left: 240px; min-height: 100vh; display: flex; flex-direction: column; }
.page-header-row { display: flex; align-items: center; justify-content: space-between; gap: 12px; margin-bottom: 16px; flex-wrap: wrap; }
.page-header-left { display: flex; align-items: center; gap: 10px; flex: 1; min-width: 0; }

.email-pill { display: inline-flex; align-items: center; gap: 6px; background: #f4f4f8; border: 1px solid #e4e3f0; border-radius: 8px; padding: 5px 11px; font-size: 12px; color: #4a4868; }
.email-pill svg { width: 13px; height: 13px; flex-shrink: 0; }
.copy-btn { background: none; border: none; cursor: pointer; padding: 0; color: #8b8aaa; display: flex; align-items: center; transition: color 0.15s; }
.copy-btn:hover { color: #3c3489; }
.copy-btn svg { width: 13px; height: 13px; }
.btn-add { padding: 6px 14px; border-radius: 7px; font-size: 13px; font-weight: 500; cursor: pointer; background: #3c3489; color: white; border: none; font-family: 'DM Sans', sans-serif; display: flex; align-items: center; gap: 6px; white-space: nowrap; }
.btn-add:hover { background: #26215c; }
.btn-add svg { width: 14px; height: 14px; }

.content { padding: 24px 28px; display: flex; flex-direction: column; gap: 20px; }

.section-label { font-size: 11px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.06em; color: #8b8aaa; margin-bottom: 10px; }

.intake-info { background: #f0effe; border: 1px solid #c4c0f0; border-radius: 12px; padding: 14px 18px; display: flex; align-items: flex-start; gap: 12px; }
.intake-info-icon { flex-shrink: 0; margin-top: 2px; }
.intake-info-title { font-size: 13px; font-weight: 600; color: #3c3489; margin-bottom: 3px; }
.intake-info-body { font-size: 12px; color: #534ab7; line-height: 1.6; }
.intake-info-row { margin-top: 10px; display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
.intake-info-label { font-size: 12px; color: #534ab7; }
.intake-info-email { display: inline-flex; align-items: center; gap: 6px; background: white; border: 1px solid #c4c0f0; border-radius: 7px; padding: 4px 10px; font-size: 12px; font-weight: 600; color: #3c3489; }
.intake-info-copy { background: none; border: none; cursor: pointer; padding: 0; display: flex; align-items: center; color: #8b8aaa; }
.intake-info-copy:hover { color: #3c3489; }

.setup-toggle { margin-top: 10px; background: none; border: none; padding: 4px 0; cursor: pointer; font-family: 'DM Sans', sans-serif; font-size: 12px; font-weight: 600; color: #3c3489; display: inline-flex; align-items: center; gap: 6px; }
.setup-toggle:hover { color: #26215c; }
.setup-chevron { width: 12px; height: 12px; transition: transform 0.2s; }
.setup-chevron-open { transform: rotate(180deg); }
.setup-panel { margin-top: 10px; background: rgba(255,255,255,0.6); border: 1px solid #c4c0f0; border-radius: 8px; padding: 12px 14px; display: flex; flex-direction: column; gap: 10px; }
.setup-step { display: flex; gap: 10px; align-items: flex-start; }
.setup-step-num { width: 20px; height: 20px; border-radius: 50%; background: #3c3489; color: white; font-size: 11px; font-weight: 700; display: flex; align-items: center; justify-content: center; flex-shrink: 0; margin-top: 1px; }
.setup-step-body { flex: 1; }
.setup-step-title { font-size: 12px; font-weight: 600; color: #1c1b2e; margin-bottom: 2px; }
.setup-step-text { font-size: 12px; color: #534ab7; line-height: 1.5; }
.setup-step-text strong { color: #3c3489; font-weight: 600; }
.setup-note { font-size: 11px; color: #8b8aaa; padding: 8px 10px; background: rgba(196,192,240,0.15); border-radius: 6px; line-height: 1.5; }
.setup-note strong { color: #4a4868; font-weight: 600; }

.queue-card { background: white; border: 1px solid #e4e3f0; border-radius: 12px; overflow: hidden; }
.queue-header { padding: 13px 18px; border-bottom: 1px solid #e4e3f0; display: flex; align-items: center; justify-content: space-between; }
.queue-title { font-size: 14px; font-weight: 600; color: #1c1b2e; }
.badge-count { font-size: 11px; font-weight: 500; padding: 2px 10px; border-radius: 99px; background: #eeedfe; color: #3c3489; transition: all 0.3s; }
.badge-count.badge-empty { background: #e1f5ee; color: #085041; }

.invoice-row { display: grid; grid-template-columns: 2fr 0.9fr 1.1fr 1fr; gap: 14px; padding: 13px 18px; border-bottom: 1px solid #f0eff8; align-items: center; transition: background 0.1s; }
.invoice-row:last-child { border-bottom: none; }
.invoice-row:hover { background: #faf9ff; }
.inv-from { font-size: 13px; font-weight: 600; color: #1c1b2e; }
.inv-sub { font-size: 11px; color: #8b8aaa; margin-top: 2px; display: flex; align-items: center; gap: 5px; flex-wrap: wrap; }
.inv-attach { display: inline-flex; align-items: center; gap: 3px; color: #3c3489; cursor: pointer; text-decoration: none; }
.inv-attach svg { width: 11px; height: 11px; }
.inv-amount { font-size: 13px; font-weight: 600; color: #1c1b2e; }
.inv-date { font-size: 11px; color: #8b8aaa; margin-top: 2px; }
.prop-select { width: 100%; padding: 6px 10px; border-radius: 7px; border: 1px solid #e4e3f0; background: white; color: #1c1b2e; font-size: 12px; font-family: 'DM Sans', sans-serif; cursor: pointer; transition: border-color 0.15s; }
.prop-select:focus { outline: none; border-color: #c4c0f0; }
.prop-select.error { border-color: #E24B4A; }
.row-actions { display: flex; gap: 6px; justify-content: flex-end; align-items: center; }
.btn-allocate { padding: 6px 13px; border-radius: 7px; font-size: 12px; font-weight: 500; cursor: pointer; background: #3c3489; color: white; border: none; font-family: 'DM Sans', sans-serif; white-space: nowrap; transition: background 0.15s; }
.btn-allocate:hover:not(:disabled) { background: #26215c; }
.btn-allocate:disabled { opacity: 0.6; cursor: not-allowed; }
.btn-dismiss { padding: 6px 10px; border-radius: 7px; font-size: 12px; cursor: pointer; background: none; border: 1px solid #e4e3f0; color: #8b8aaa; font-family: 'DM Sans', sans-serif; transition: all 0.15s; }
.btn-dismiss:hover:not(:disabled) { border-color: #c4c0f0; color: #4a4868; }
.btn-dismiss:disabled { opacity: 0.6; cursor: not-allowed; }
.btn-delete { display: inline-flex; align-items: center; justify-content: center; padding: 6px; border-radius: 7px; cursor: pointer; background: none; border: 1px solid #e4e3f0; color: #b74a4a; font-family: 'DM Sans', sans-serif; transition: all 0.15s; }
.btn-delete:hover:not(:disabled) { background: #fbeaea; border-color: #f0c4c4; color: #922; }
.btn-delete:disabled { opacity: 0.6; cursor: not-allowed; }
.btn-delete svg { width: 14px; height: 14px; }
.lr-actions { display: flex; justify-content: flex-end; align-items: center; }
.empty-queue { padding: 2.5rem 1rem; text-align: center; color: #8b8aaa; font-size: 13px; }
.empty-queue svg { width: 22px; height: 22px; display: block; margin: 0 auto 8px; }

.log-card { background: white; border: 1px solid #e4e3f0; border-radius: 12px; overflow: hidden; }
.log-toolbar { padding: 13px 18px; border-bottom: 1px solid #e4e3f0; display: flex; align-items: center; justify-content: space-between; gap: 10px; flex-wrap: wrap; }
.log-title { font-size: 14px; font-weight: 600; color: #1c1b2e; }
.log-filter { padding: 5px 10px; border-radius: 7px; font-size: 12px; border: 1px solid #e4e3f0; background: white; color: #1c1b2e; font-family: 'DM Sans', sans-serif; cursor: pointer; }
.log-filter:focus { outline: none; border-color: #c4c0f0; }

.col-heads { display: grid; grid-template-columns: 1.8fr 1fr 0.8fr 0.8fr 40px; gap: 12px; padding: 9px 18px; background: #f9f9fb; border-bottom: 1px solid #e4e3f0; }
.ch { font-size: 10px; font-weight: 600; color: #8b8aaa; text-transform: uppercase; letter-spacing: 0.05em; }

.log-row { display: grid; grid-template-columns: 1.8fr 1fr 0.8fr 0.8fr 40px; gap: 12px; padding: 12px 18px; border-bottom: 1px solid #f0eff8; align-items: center; transition: background 0.1s; }
.log-row:last-child { border-bottom: none; }
.log-row:hover { background: #faf9ff; }
.lr-supplier { font-size: 13px; font-weight: 600; color: #1c1b2e; }
.lr-desc { font-size: 11px; color: #8b8aaa; margin-top: 2px; }
.lr-prop { font-size: 12px; color: #1c1b2e; display: flex; align-items: center; gap: 6px; flex-wrap: wrap; }
.group-tag { font-size: 10px; font-weight: 600; padding: 1px 7px; border-radius: 99px; background: #eeedfe; color: #3c3489; text-transform: uppercase; letter-spacing: 0.05em; }
.lr-amount { font-size: 13px; font-weight: 600; color: #1c1b2e; }
.lr-date { font-size: 12px; color: #8b8aaa; }

.modal-overlay { position: fixed; inset: 0; background: rgba(0, 0, 0, 0.35); display: flex; align-items: center; justify-content: center; z-index: 200; padding: 20px; }
.modal-card { background: white; border-radius: 12px; width: 100%; max-width: 480px; overflow: hidden; display: flex; flex-direction: column; box-shadow: 0 20px 40px rgba(0,0,0,0.15); font-family: 'DM Sans', sans-serif; }
.modal-header { padding: 16px 20px; border-bottom: 1px solid #e4e3f0; display: flex; align-items: center; justify-content: space-between; }
.modal-title { font-size: 15px; font-weight: 600; color: #1c1b2e; }
.modal-close { background: none; border: none; cursor: pointer; color: #8b8aaa; padding: 0; display: flex; align-items: center; }
.modal-close:hover { color: #1c1b2e; }
.modal-close svg { width: 14px; height: 14px; }
.modal-body { padding: 18px 20px; display: flex; flex-direction: column; gap: 14px; }
.form-row { display: flex; flex-direction: column; gap: 6px; flex: 1; }
.form-row label { font-size: 11px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.05em; color: #8b8aaa; }
.form-row input { padding: 8px 11px; border-radius: 7px; border: 1px solid #e4e3f0; font-size: 13px; font-family: 'DM Sans', sans-serif; color: #1c1b2e; background: white; }
.form-row input:focus { outline: none; border-color: #c4c0f0; }
.form-row-inline { display: flex; gap: 12px; }
.file-hint { font-size: 11px; color: #4a4868; }
.form-error { padding: 8px 11px; background: #fdecea; border: 1px solid #f5b5b3; border-radius: 7px; color: #7a1f1e; font-size: 12px; }
.modal-footer { padding: 14px 20px; border-top: 1px solid #e4e3f0; display: flex; justify-content: flex-end; gap: 8px; }
.btn-cancel { padding: 7px 14px; border-radius: 7px; font-size: 13px; cursor: pointer; background: white; border: 1px solid #e4e3f0; color: #4a4868; font-family: 'DM Sans', sans-serif; }
.btn-cancel:hover { border-color: #c4c0f0; }
.btn-primary { padding: 7px 16px; border-radius: 7px; font-size: 13px; font-weight: 500; cursor: pointer; background: #3c3489; color: white; border: none; font-family: 'DM Sans', sans-serif; }
.btn-primary:hover:not(:disabled) { background: #26215c; }
.btn-primary:disabled { opacity: 0.6; cursor: not-allowed; }

.allocate-summary { background: #faf9ff; border: 1px solid #e4e3f0; border-radius: 8px; padding: 10px 12px; display: flex; flex-direction: column; gap: 4px; margin-bottom: 4px; }
.allocate-summary-row { display: flex; justify-content: space-between; gap: 12px; font-size: 12px; }
.allocate-summary-label { color: #8b8aaa; text-transform: uppercase; font-size: 10px; font-weight: 600; letter-spacing: 0.05em; }
.allocate-summary-value { color: #1c1b2e; font-weight: 500; text-align: right; }

.vat-toggle-group { display: flex; gap: 8px; }
.vat-toggle-option { flex: 1; padding: 9px 11px; border-radius: 7px; border: 1px solid #e4e3f0; background: white; font-size: 12px; color: #4a4868; cursor: pointer; text-align: center; transition: all 0.15s; user-select: none; }
.vat-toggle-option:hover { border-color: #c4c0f0; }
.vat-toggle-option.active { border-color: #3c3489; background: #eeedfe; color: #3c3489; font-weight: 600; }
.form-hint { font-size: 11px; color: #8b8aaa; margin: 4px 0 0; line-height: 1.5; }
.form-row select { padding: 8px 11px; border-radius: 7px; border: 1px solid #e4e3f0; font-size: 13px; font-family: 'DM Sans', sans-serif; color: #1c1b2e; background: white; cursor: pointer; }
.form-row select:focus { outline: none; border-color: #c4c0f0; }

@media (max-width: 1024px) {
  .main { margin-left: 0; }
  .content { padding: 20px 18px; }
}
@media (max-width: 768px) {
  .content { padding: 16px 12px; gap: 14px; }
  .invoice-row { grid-template-columns: 1fr 1fr; gap: 10px; padding: 12px 14px; }
  .invoice-row > div:nth-child(3) { grid-column: 1 / -1; }
  .invoice-row > div:nth-child(4) { grid-column: 1 / -1; }
  .col-heads { grid-template-columns: 1fr 1fr; padding: 8px 14px; }
  .col-heads .ch:nth-child(3), .col-heads .ch:nth-child(4) { display: none; }
  .log-row { grid-template-columns: 1fr 1fr; gap: 10px; padding: 10px 14px; }
  .log-row > div:nth-child(3), .log-row > div:nth-child(4) { display: none; }
}
@media (max-width: 768px) {
  .modal-overlay { padding: 0; }
  .modal-card { max-width: 96vw; }
  .modal-header, .modal-body, .modal-footer { padding: 14px 16px; }
  .modal-footer { flex-wrap: wrap; gap: 8px; }
  .modal-footer .btn { flex: 1; justify-content: center; }
}

@media (max-width: 480px) {
  .content { padding: 12px 10px; }
  .email-pill { display: none; }
  .invoice-row, .log-row { grid-template-columns: 1fr; }
  .col-heads { display: none; }
  .modal-card { max-width: 100vw; width: 100vw; max-height: 100vh; border-radius: 0; }
}
</style>
